import React, { useEffect, useRef, useState } from "react";

import {
  inputStateForValue,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsFetching,
  queryIsLoading,
  useGetCropFieldHectares,
  useGetFootprintingCropFertilizersMineral,
  useGetFootprintingCropFertilizersOrganic,
  useGetFootprintingCropPrimaryDataById,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useKeyedFormList,
  useMutateFootprintingCropFertilizersMinerals,
  useMutateFootprintingCropFertilizersOrganics,
  useMutateFootprintingCropPrimaryData,
  usePracticeManagementOrganicFertilizerParameters,
  useTranslation,
} from "utils";

import { HARD_INPUT_STATES, HARVEST_UNIT_VALUE, getUnitDisplayLabel, toTonnesPerHa } from "consts";

import {
  ENUM_FERTILIZER_UNITS,
  FootprintingCropFertilizer,
  ORGANIC_AMOUNT_UNITS,
  PRACTICE_SLUG,
  ZERO_UUID,
  resolveNValueUnitTypeFromOrganicFertAmountUnit,
} from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Option,
  PageSpin,
  SanityCheck,
  Select,
  Tooltip,
  Typography,
} from "components";

import { MineralFertilizerOptionLabel } from "pages/app/all-field-view/components/forms/fertilizers/MineralFertilizerOptionLabel";

type CropFertilizersFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
};

const fertilizerPlaceholder: FootprintingCropFertilizer = {
  id: undefined,
  fertilizerId: "",
  amount: null,
  amountUnit: null,
};

const organicFertilizerPlaceholder: FootprintingCropFertilizer = {
  ...fertilizerPlaceholder,
  nValue: null,
};

const CropFertilizersForm = ({ farmId, cropId, year, primaryDataId, readOnly }: CropFertilizersFormProps) => {
  const { t } = useTranslation(["footprintingCrop", "practices"]);
  const formRef = useRef<FormInstance>(null);

  const [mineralFertilizerActive, setMineralFertilizerActive] = useState<boolean | null>(null);
  const [organicFertilizerActive, setOrganicFertilizerActive] = useState<boolean | null>(null);
  const [limeT, setLimeT] = useState<number | null>(null);

  // GET Queries
  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");
  const organicFertilizerParametersQuery = usePracticeManagementOrganicFertilizerParameters();
  // The amounts entered here are totals for the crop, but the amount bounds are in t/ha
  const cropHectares = useGetCropFieldHectares({ farmId, cropId, year, fieldIds: cropPrimaryDataQuery.data?.fieldIds ?? [] });

  const mineralFertilizerDataQuery = useGetPracticeMineralFertilizers({ enabled: true });
  const organicFertilizerDataQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const cropFertilizersMineralQuery = useGetFootprintingCropFertilizersMineral(primaryDataId ?? "");
  const cropFertilizersOrganicQuery = useGetFootprintingCropFertilizersOrganic(primaryDataId ?? "");
  const isLoading = [cropPrimaryDataQuery, cropFertilizersMineralQuery, cropFertilizersOrganicQuery].some(queryIsLoading);
  const keyedMineralFertilizers = useKeyedFormList<FootprintingCropFertilizer>(formRef, { isReady: !isLoading });
  const keyedOrganicFertilizers = useKeyedFormList<FootprintingCropFertilizer>(formRef, { isReady: !isLoading });

  // Mutations
  const { mutate: mutateCropFertilizersMinerals } = useMutateFootprintingCropFertilizersMinerals(
    farmId,
    cropId,
    year,
    keyedMineralFertilizers.payload.filter((f) => !!f.fertilizerId),
    primaryDataId ?? ""
  );

  const { mutate: mutateCropFertilizersOrganics } = useMutateFootprintingCropFertilizersOrganics(
    farmId,
    cropId,
    year,
    keyedOrganicFertilizers.payload.filter((f) => !!f.fertilizerId),
    primaryDataId ?? ""
  );

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      limeT,
      mineralFertilizerActive,
      organicFertilizerActive,
    },
    primaryDataId
  );

  const isFetching = [cropPrimaryDataQuery, cropFertilizersMineralQuery, cropFertilizersOrganicQuery].some(queryIsFetching);

  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess && !isLoading && !isFetching) {
      setMineralFertilizerActive(cropPrimaryDataQuery.data.mineralFertilizerActive ?? null);
      setOrganicFertilizerActive(cropPrimaryDataQuery.data.organicFertilizerActive ?? null);
      setLimeT(cropPrimaryDataQuery.data.limeT ?? null);

      formRef.current?.setFieldsValue({
        mineralFertilizerActive: cropPrimaryDataQuery.data.mineralFertilizerActive ?? null,
        organicFertilizerActive: cropPrimaryDataQuery.data.organicFertilizerActive ?? null,
        limeT: cropPrimaryDataQuery.data.limeT ?? null,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = (["mineralFertilizerActive", "organicFertilizerActive", "limeT"] as const).some(
        (field) => cropPrimaryDataQuery.data?.[field] !== null
      );

      if (fieldsToValidate) {
        keyedMineralFertilizers.actions.requestRehydrationValidation();
        keyedOrganicFertilizers.actions.requestRehydrationValidation();
      }
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess, isLoading, isFetching, keyedMineralFertilizers.actions, keyedOrganicFertilizers.actions]);

  useEffect(() => {
    if (cropFertilizersMineralQuery.data && !isLoading) {
      keyedMineralFertilizers.actions.hydrate(
        cropFertilizersMineralQuery.data.length
          ? cropFertilizersMineralQuery.data
              .filter((application) => application.id !== undefined)
              .map((application) => ({
                id: application.id,
                fertilizerId: application.fertilizerId,
                amount: application.amount,
                amountUnit: application.amountUnit,
              }))
          : [fertilizerPlaceholder]
      );
    }
  }, [cropFertilizersMineralQuery.data, isLoading, keyedMineralFertilizers.actions]);

  useEffect(() => {
    if (cropFertilizersOrganicQuery.data && !isLoading) {
      keyedOrganicFertilizers.actions.hydrate(
        cropFertilizersOrganicQuery.data.length
          ? cropFertilizersOrganicQuery.data
              .filter((application) => application.id !== undefined)
              .map((application) => ({
                id: application.id,
                fertilizerId: application.fertilizerId,
                nValue: application.nValue,
                amount: application.amount,
                amountUnit: application.amountUnit,
              }))
          : [organicFertilizerPlaceholder]
      );
    }
  }, [cropFertilizersOrganicQuery.data, isLoading, keyedOrganicFertilizers.actions]);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropPrimaryData();
        mutateCropFertilizersMinerals();
        mutateCropFertilizersOrganics();
      }
    },
    [isLoading, mutateCropFertilizersMinerals, mutateCropFertilizersOrganics, mutateCropPrimaryData, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Form dataTestId="footprinting-crop-fertilizers-form" ref={formRef} preserve={false}>
      <Flex vertical gap={16}>
        <Card border className="padding-100">
          <Typography variant="h3" className="padding-100">
            {t("cropFertilizerForm.synthetic.subtitle")}
          </Typography>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <Typography variant="paragraph-m">
              {t("cropFertilizerForm.synthetic.question")}
              {true && <span className="required-asterisk">*</span>}
              <Tooltip title={t("cropFertilizerForm.tooltips.syntheticFertilizerQuestion")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </Typography>
            <div className="fertilizer-radio-container">
              <KlimRadioGroup
                required
                name="mineralFertilizerActive"
                dataTestId="footprinting-crop-mineral-fertilizer-radio"
                disabled={readOnly}
                vertical={false}
                type="tertiary"
                size="large"
                onChange={({ target }) => {
                  setMineralFertilizerActive(target.value);
                }}
                value={mineralFertilizerActive}
                defaultValue={mineralFertilizerActive}
                optionType="button"
                options={[true, false].map((opt) => ({
                  label: t(`footprinting:radioAnswer.${opt}`),
                  value: opt,
                }))}
              />
            </div>
          </Flex>
          {mineralFertilizerActive &&
            keyedMineralFertilizers.entries.map((fertilizer, index) => {
              const selectGroupWithIndex = `footprinting-crop-mineral-fertilizer-select-${index}`;
              const selectGroupWithKey = `footprinting-crop-mineral-fertilizer-select-${fertilizer.clientKey}`;

              return (
                <Form.Item key={fertilizer.clientKey} className="padding-100">
                  <Flex vertical gap={16}>
                    <Flex justify="space-between" vertical={false}>
                      <Typography variant="h5" style={{ flex: 1 }}>
                        {t("cropFertilizerForm.application.title") + (index + 1)}
                      </Typography>
                      <IconButton
                        type="tertiary"
                        dataTestId={`${selectGroupWithIndex}-delete`}
                        disabled={readOnly}
                        size="small"
                        iconName="tabler/trash"
                        onClick={() => {
                          keyedMineralFertilizers.actions.remove(fertilizer.clientKey);
                          if (keyedMineralFertilizers.entries.length === 1) setMineralFertilizerActive(null);
                        }}
                      />
                    </Flex>
                    <Select
                      disabled={readOnly}
                      name={selectGroupWithKey}
                      dataTestId={selectGroupWithIndex}
                      label={t("cropFertilizerForm.application.productName")}
                      tooltip={t("cropFertilizerForm.tooltips.syntheticProductName")}
                      rules={[{ required: true, message: "" }]}
                      initialValue={fertilizer.fertilizerId}
                      value={fertilizer.fertilizerId}
                      onChange={(v) => keyedMineralFertilizers.actions.update(fertilizer.clientKey, { fertilizerId: v })}
                      dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                      showSearch
                    >
                      {(mineralFertilizerDataQuery?.data ?? [])
                        .filter(({ id }) => id !== ZERO_UUID)
                        .map((fertilizer) => (
                          <Option
                            disabled={readOnly}
                            key={`${selectGroupWithIndex}-${fertilizer.id}`}
                            data-testid={`${selectGroupWithIndex}-${fertilizer.id}`}
                            value={fertilizer.id}
                          >
                            <MineralFertilizerOptionLabel
                              name={t(`${PRACTICE_SLUG.MINERAL_FERTILIZERS}.${fertilizer.translationSlug}`, { ns: "practices" })}
                              hasUreaseInhibitor={fertilizer.hasUreaseInhibitor}
                              hasNitrificationInhibitor={fertilizer.hasNitrificationInhibitor}
                              hasLowCarbonEmissions={fertilizer.hasLowCarbonEmissions}
                            />
                          </Option>
                        ))}
                    </Select>

                    <InputNumberWithUnitSelector
                      className="floating-label"
                      disabled={readOnly}
                      name={`${selectGroupWithKey}-amount`}
                      dataTestId={`${selectGroupWithIndex}-amount`}
                      label={t("cropFertilizerForm.application.amount")}
                      unitLabel={t("unit", { ns: "footprinting" })}
                      tooltip={t("cropFertilizerForm.tooltips.syntheticAmountUsed")}
                      units={["tonnes", "kilograms", "cubic_meters", "liters"]}
                      value={fertilizer.amount}
                      initialValue={fertilizer.amount}
                      unitValue={fertilizer.amountUnit}
                      onChange={(v, unit) =>
                        keyedMineralFertilizers.actions.update(fertilizer.clientKey, { amount: v, amountUnit: unit as ENUM_FERTILIZER_UNITS })
                      }
                      rules={[
                        { required: true, message: t("cropFertilizerForm.application.amountError") },
                        { type: "number", message: t("cropFertilizerForm.error.totalAmountRequiredError") },
                        {
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || isValidNonnegativeNumber(v)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(t("cropFertilizerForm.application.amountError")));
                          },
                        },
                      ]}
                    />
                  </Flex>
                </Form.Item>
              );
            })}
          {mineralFertilizerActive && (
            <Button
              type="text"
              disabled={readOnly}
              onClick={() => {
                keyedMineralFertilizers.actions.add(fertilizerPlaceholder);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("cropFertilizerForm.synthetic.add")}
            </Button>
          )}
        </Card>
        <Card border className="padding-100">
          <Typography variant="h3" className="padding-100">
            {t("cropFertilizerForm.organic.subtitle")}
          </Typography>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <Typography variant="paragraph-m" style={{ maxWidth: "60%" }}>
              {t("cropFertilizerForm.organic.question")}
              {true && <span className="required-asterisk">*</span>}
              <Tooltip title={t("cropFertilizerForm.tooltips.organicFertilizerQuestion")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </Typography>
            <KlimRadioGroup
              required
              name="organicFertilizerActive"
              dataTestId="footprinting-crop-organic-fertilizer-radio"
              disabled={readOnly}
              vertical={false}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setOrganicFertilizerActive(target.value);
              }}
              value={organicFertilizerActive}
              defaultValue={organicFertilizerActive}
              optionType="button"
              options={[true, false].map((opt) => ({
                label: t(`footprinting:radioAnswer.${opt}`),
                value: opt,
              }))}
            />
          </Flex>
          {organicFertilizerActive &&
            keyedOrganicFertilizers.entries.map((fertilizer, index) => {
              const ofParameter = organicFertilizerParametersQuery.data?.find((param) => param.fertilizerId === fertilizer.fertilizerId);
              const amountInTonnesPerHa =
                fertilizer.amount && fertilizer.amountUnit && cropHectares > 0 ? toTonnesPerHa(fertilizer.amount / cropHectares, fertilizer.amountUnit) : null;
              const selectGroupWithIndex = `footprinting-crop-organic-fertilizer-select-${index}`;
              const selectGroupWithKey = `footprinting-crop-organic-fertilizer-select-${fertilizer.clientKey}`;

              return (
                <Form.Item key={fertilizer.clientKey} className="padding-100">
                  <Flex vertical gap={16}>
                    <Flex justify="space-between" vertical={false}>
                      <Typography variant="h5" style={{ flex: 1 }}>
                        {t("cropFertilizerForm.application.title") + (index + 1)}
                      </Typography>
                      <IconButton
                        disabled={readOnly}
                        type="tertiary"
                        size="small"
                        dataTestId={`${selectGroupWithIndex}-delete`}
                        iconName="tabler/trash"
                        onClick={() => {
                          keyedOrganicFertilizers.actions.remove(fertilizer.clientKey);
                          if (keyedOrganicFertilizers.entries.length === 1) setOrganicFertilizerActive(null);
                        }}
                      />
                    </Flex>
                    <Select
                      disabled={readOnly}
                      name={selectGroupWithKey}
                      dataTestId={selectGroupWithIndex}
                      label={t("cropFertilizerForm.application.productName")}
                      tooltip={t("cropFertilizerForm.tooltips.organicProductName")}
                      rules={[{ required: false, message: "" }]}
                      initialValue={fertilizer.fertilizerId}
                      value={fertilizer.fertilizerId}
                      onChange={(v) => {
                        keyedOrganicFertilizers.actions.update(fertilizer.clientKey, { fertilizerId: v, nValue: null });
                        formRef.current?.setFieldValue(`${selectGroupWithIndex}-nitrogen`, null);
                      }}
                      dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                      showSearch
                    >
                      {(organicFertilizerDataQuery?.data ?? [])
                        .filter(({ id }) => id !== ZERO_UUID)
                        .map((fertilizer) => (
                          <Option
                            disabled={readOnly}
                            key={`${selectGroupWithIndex}-${fertilizer.id}`}
                            data-testid={`${selectGroupWithIndex}-${fertilizer.id}`}
                            value={fertilizer.id}
                          >
                            {fertilizer.name}
                          </Option>
                        ))}
                    </Select>

                    <Flex vertical gap={16}>
                      <InputNumberWithUnitSelector
                        name={`${selectGroupWithKey}-amount`}
                        dataTestId={`${selectGroupWithIndex}-amount`}
                        disabled={readOnly}
                        label={t("cropFertilizerForm.application.amount")}
                        unitLabel={t("unit", { ns: "footprinting" })}
                        tooltip={t("cropFertilizerForm.tooltips.organicAmountUsed")}
                        units={[...ORGANIC_AMOUNT_UNITS]}
                        value={fertilizer.amount}
                        initialValue={fertilizer.amount}
                        unitValue={fertilizer.amountUnit}
                        onChange={(v, unit) =>
                          keyedOrganicFertilizers.actions.update(fertilizer.clientKey, { amount: v, amountUnit: unit as ENUM_FERTILIZER_UNITS })
                        }
                        validateFirst={true}
                        rules={[
                          { required: false, message: t("cropFertilizerForm.application.amountError") },
                          { type: "number", message: t("cropFertilizerForm.application.amountError") },
                          {
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || isValidNonnegativeNumber(v)) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error(t("cropFertilizerForm.application.amountError")));
                            },
                          },
                        ]}
                      />
                      {amountInTonnesPerHa !== null && (
                        <SanityCheck
                          inputValue={amountInTonnesPerHa}
                          inputRange={ofParameter?.amountAppliedTonnesPerHectare}
                          unit={HARVEST_UNIT_VALUE.tonnesPerHa}
                        />
                      )}
                      <InputNumber
                        name={`${selectGroupWithKey}-nitrogen`}
                        className="hide-error-message"
                        validateStatus={
                          fertilizer.nValue && HARD_INPUT_STATES.includes(inputStateForValue(fertilizer.nValue, ofParameter?.nitrogenContentKgPerTon))
                            ? "error"
                            : undefined
                        }
                        dataTestId={`${selectGroupWithIndex}-nitrogen`}
                        disabled={readOnly}
                        label={t("cropFertilizerForm.application.nitrogen")}
                        tooltip={t("cropFertilizerForm.tooltips.nitrogenContent")}
                        suffix={getUnitDisplayLabel(resolveNValueUnitTypeFromOrganicFertAmountUnit(fertilizer.amountUnit))}
                        value={fertilizer.nValue}
                        initialValue={fertilizer.nValue}
                        onChange={(v) => keyedOrganicFertilizers.actions.update(fertilizer.clientKey, { nValue: v })}
                        validateFirst={true}
                        precision={2}
                        guidanceText={ofParameter?.nitrogenContentKgPerTon}
                      />
                    </Flex>
                    {fertilizer.nValue ? (
                      <SanityCheck
                        inputValue={fertilizer.nValue}
                        inputRange={ofParameter?.nitrogenContentKgPerTon}
                        unit={resolveNValueUnitTypeFromOrganicFertAmountUnit(fertilizer.amountUnit)}
                      />
                    ) : null}
                  </Flex>
                </Form.Item>
              );
            })}
          {organicFertilizerActive && (
            <Button
              type="text"
              disabled={readOnly}
              onClick={() => {
                keyedOrganicFertilizers.actions.add(organicFertilizerPlaceholder);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("cropFertilizerForm.organic.add")}
            </Button>
          )}
        </Card>
        <Card border className="padding-100">
          <Typography variant="h3" className="padding-100">
            {t("cropFertilizerForm.lime.subtitle")}
          </Typography>
          <Form.Item className="padding-100">
            <InputNumber
              disabled={readOnly}
              name="limeT"
              dataTestId="footprinting-crop-fertilizers-form-caco3-input"
              label={t("cropFertilizerForm.application.lime")}
              tooltip={t("cropFertilizerForm.tooltips.lime")}
              suffix="t"
              value={limeT}
              initialValue={limeT}
              precision={2}
              onChange={(v) => setLimeT(v)}
              validateFirst={true}
              rules={[
                { required: true, message: t("cropFertilizerForm.application.amountError") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || isValidNonnegativeNumber(v)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t("cropFertilizerForm.application.amountError")));
                  },
                },
              ]}
            />
          </Form.Item>
        </Card>
      </Flex>
    </Form>
  );
};

CropFertilizersForm.Key = "crop.fertilizers";
export { CropFertilizersForm };
